import React, { FC, MouseEventHandler, useContext } from 'react'

import { useI18n } from '@shopify/react-i18n'
import { Box, Div, Icon, LazyBackgroundImage, Pill, Row, Text } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'

import FilterContext from '@/components/pages/production/components/context'
import LayoutContext from '@/context/layout/context'
import ListingsContext from '@/context/listings'
import { buildResponsiveHighlightedMapImageUrls, buildResponsiveSectionImageUrls } from '@/context/listings/utils'
import { InstantDeliveryWithContainerIcon, SeatSaverWithContainerIcon } from '@/design-system/custom-icons'
import { usePromoPrice } from '@/hooks/use-promo-price'
import type { ComposedListing } from '@/types/app-types'
import { getListingPrice } from '@/utils/i18n'

import { LISTING_ROW_IMAGE_HEIGHT, LISTING_ROW_IMAGE_WIDTH } from '../../constants'
import ListingBadges from '../listing-badges'

import styles from './styles.module.scss'

interface ListingRowProps extends ComposedListing {
    showUsd: boolean
    onClick?: MouseEventHandler<HTMLElement>
    onMouseEnter?: MouseEventHandler<HTMLElement>
}

const ViewedBadge = ({ isAbsolute }: { isAbsolute: boolean }) => {
    const absoluteClass = isAbsolute ? styles.absoluteViewedBadge : ''
    return (
        <Pill importance="none" className={classNames(styles.pillBadge, styles.viewedBadge, absoluteClass)}>
            Viewed
        </Pill>
    )
}

const GreatDeal = () => (
    <Pill importance="success" className={styles.pillBadge}>
        Great Deal
    </Pill>
)

const ListingRow: FC<ListingRowProps> = ({
    price,
    defaultQuantity,
    quantityRangeText,
    section,
    sectionId,
    sectionColor,
    row,
    showUsd,
    inGroup,
    isGreatDeal,
    isPreferredSeller,
    isInstantDelivery,
    isZoneSeating,
    isRecentlyViewed,
    onClick,
    onMouseEnter,
    id,
    allInPrice,
    badges,
}) => {
    const { state: filterState } = useContext(FilterContext)
    const { showAllInPrice } = filterState
    const {
        selectors: { seatViewObject, globalDataSelector, i18nListingsData },
    } = useContext(ListingsContext)
    const {
        state: { data: mapData },
    } = useContext(LayoutContext)

    const showSectionSeatView = !!mapData?.jsonFileName

    const [i18n] = useI18n()

    const listingPrice = filterState.showAllInPrice ? allInPrice : price

    const discountPrice = usePromoPrice(
        Number(listingPrice),
        filterState.quantityFilterState.quantity || defaultQuantity,
    )
    const hasDiscountPrice = Boolean(discountPrice)
    const showDiscountPricing = hasDiscountPrice

    const sectionViewPath = seatViewObject?.ticketSections[sectionId || '']?.section?.sip ?? ''

    const listingRowImageUrl = sectionViewPath
        ? buildResponsiveSectionImageUrls({
              path: sectionViewPath,
              height: LISTING_ROW_IMAGE_HEIGHT,
              width: LISTING_ROW_IMAGE_WIDTH,
          })
        : buildResponsiveHighlightedMapImageUrls({
              layoutId: globalDataSelector?.[0].layoutId || '',
              sectionId,
              height: LISTING_ROW_IMAGE_HEIGHT,
              width: LISTING_ROW_IMAGE_WIDTH,
          })
    const hasSeatView = !!sectionViewPath

    return (
        <div
            className={styles.listingRowContainer}
            id="row-container"
            data-testid="listing-row-container"
            style={{ borderLeft: `6px solid ${showSectionSeatView ? sectionColor : 'transparent'}` }}
        >
            <Row
                role="button"
                align="stretch"
                tabIndex={-1}
                className={styles.listingRow}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                data-testid={id}
                data-sectionid={sectionId}
            >
                {showSectionSeatView && (
                    <Div className={styles.sectionPreview}>
                        <div
                            className={classNames({ [styles.overlayContainer]: !hasSeatView && !!isRecentlyViewed })}
                        />
                        {!!isRecentlyViewed && <ViewedBadge isAbsolute />}
                        <LazyBackgroundImage
                            url={listingRowImageUrl}
                            className={classNames(styles.image, { [styles.seatViewImage]: hasSeatView })}
                            data-testid={hasSeatView ? 'seat-view-thumbnail' : 'highlighted-map-thumbnail'}
                        />
                    </Div>
                )}
                <Box display="flex" flexGrow className={styles.sectionColumn}>
                    <Box paddingLeft={3} paddingTop={1} paddingBottom={1} className={styles.sectionContent}>
                        <Box display="flex" align="center">
                            <Text
                                size="md"
                                as="div"
                                weight="regular-bold"
                                className={styles.infoLineHeight}
                                data-testid={section}
                                altFont
                                truncate
                            >
                                {section}
                            </Text>
                        </Box>
                        <Box align="center" display="flex" flexDirection="row" id={`section-${id}`}>
                            <Text size="sm" altFont className={classNames(styles.nowrap, styles.infoLineHeight)}>
                                {!inGroup && (
                                    <>
                                        <span data-testid="row" className={styles.rowText}>{` Row ${row}`}</span>{' '}
                                        <span className={styles.divider}>|</span>{' '}
                                    </>
                                )}
                                {quantityRangeText}{' '}
                            </Text>
                            <Box display="flex" paddingLeft={2} align="center">
                                {isPreferredSeller && (
                                    <Icon className={styles.superSellerIcon} type="ticket-super-seller" size="xxs" />
                                )}
                                {isInstantDelivery && (
                                    <InstantDeliveryWithContainerIcon
                                        className={styles.badgeIcon}
                                        width={16}
                                        height={16}
                                    />
                                )}
                                {isZoneSeating && (
                                    <SeatSaverWithContainerIcon className={styles.badgeIcon} width={16} height={16} />
                                )}
                            </Box>
                        </Box>
                        <Box display="flex" flexDirection="row" className={styles.badgesList}>
                            {badges?.[0] && <ListingBadges badge={badges[0]} />}
                            {!showSectionSeatView && !!isRecentlyViewed && <ViewedBadge isAbsolute={false} />}
                        </Box>
                    </Box>
                </Box>
                <Box display="flex" className={styles.priceColumn}>
                    <Box marginRight={3} className={styles.priceContent}>
                        <Box>{isGreatDeal && <GreatDeal />}</Box>
                        <Box>
                            <Text size="sm" altFont className={classNames(styles.nowrap, styles.infoLineHeight)}>
                                {showAllInPrice && <span className={styles.feelsIncluded}>Fees Incl.</span>}
                                {showDiscountPricing && (
                                    <span
                                        className={styles.beforeDiscount}
                                        data-testid="listing-price"
                                    >{`${getListingPrice(i18n, i18nListingsData, listingPrice)}`}</span>
                                )}
                            </Text>
                        </Box>
                        <Box display="flex" align="center">
                            <Text
                                size="lg"
                                as="span"
                                className={showDiscountPricing ? styles.discountAmount : styles.priceAmount}
                                data-testid={showDiscountPricing ? 'discount-price' : 'listing-price'}
                                altFont
                            >
                                {showDiscountPricing
                                    ? getListingPrice(i18n, i18nListingsData, discountPrice)
                                    : getListingPrice(i18n, i18nListingsData, listingPrice)}
                            </Text>
                            <Text
                                size="xs"
                                as="span"
                                altFont
                                className={styles.priceUnitStyle}
                                data-testid="price-style"
                            >
                                {showUsd && !i18nListingsData?.applyCurrencyConversion ? <>&nbsp;USD</> : null}&nbsp;ea
                            </Text>
                        </Box>
                    </Box>
                </Box>
            </Row>
        </div>
    )
}

export default ListingRow
